import React, { createContext, useContext, useState, useEffect } from 'react';
import api from "../services/api";

const AuthContext = createContext();

const defaultUsers = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    username: 'admin',
    password: 'admin',
    role: 'super_admin',
    companyId: null,
    subCompanyId: null,
    phone: '',
    mileage: null  // ✅ Added
  }
];

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem('currentUser');
    return stored ? JSON.parse(stored) : null;
  });

  const [users, setUsers] = useState([...defaultUsers]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {  
      const [installerResponse, adminResponse, teamLeadResponse] = await Promise.all([
        api.get("/installer").catch(err => {
          return { data: { data: [] } };
        }),
        api.get("/admin-users").catch(err => {
          console.log("⚠️ Admin API error:", err.message);
          return { data: [] };
        }),
        api.get("/teamlead").catch(err => {
          console.log("⚠️ TeamLead API error:", err.message);
          return { data: [] };
        })
      ]);

      // ✅ Map installer with mileage
      const installers = (installerResponse.data.data || []).map(user => ({
        ...user,
        role: "installer",
        companyId: user.company_id,
        subCompanyId: user.sub_company_id,
        teamLeadId: user.teamlead_id,
        mileage: user.mileage || null  // ✅ Added mileage mapping
      }));

      const admins = (adminResponse.data || []).map(user => ({
        ...user,
        role: "admin",
        companyId: user.company_id,
        mileage: null  // Admins don't have mileage
      }));

      const teamLeads = (teamLeadResponse.data || []).map(user => ({
        ...user,
        role: "teamlead",
        mileage: null  // Team leads don't have mileage
      }));

      const allUsers = [
        ...defaultUsers,
        ...admins,
        ...teamLeads,
        ...installers
      ];   
      setUsers(allUsers);
    } catch (error) {
      console.log("❌ Load Users Error:", error);
      setUsers([...defaultUsers]);
    }
  };

  const login = (username, password) => {
    if (username === 'query' && password === 'query') {
      const queryUser = {
        id: 'query-session-id-000000',
        username: 'query',
        role: 'installer',
        companyId: null,
        subCompanyId: null,
        phone: '',
        mileage: null  // ✅ Added
      };
      localStorage.setItem('currentUser', JSON.stringify(queryUser));
      setCurrentUser(queryUser);
      return { success: true, user: queryUser };
    }

    if (username === 'admin' && password === 'admin') {
      const adminUser = defaultUsers[0];
      localStorage.setItem('currentUser', JSON.stringify(adminUser));
      setCurrentUser(adminUser);
      return { success: true, user: adminUser };
    }
    
    const user = users.find(u => u.username === username && u.password === password);
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
      setCurrentUser(user);
      return { success: true, user };
    }
    return { success: false, message: 'Invalid credentials' };
  };

  const logout = () => {
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
  };

  const addUser = async (newUser) => {
    try {
      if (newUser.role === "installer") {
        await api.post("/installer", {
          username: newUser.username,
          password: newUser.password,
          phone: newUser.phone,
          company_id: newUser.companyId,
          sub_company_id: newUser.subCompanyId || null,
          teamlead_id: newUser.teamLeadId || null,
          mileage: newUser.mileage || null  // ✅ Added mileage
        });
      } else if (newUser.role === "admin") {
        await api.post("/admin-users", {
          username: newUser.username,
          email: newUser.email,
          password: newUser.password,
          company_id: newUser.companyId
        });
      } else if (newUser.role === "teamlead") {
        await api.post("/teamlead", {
          username: newUser.username,
          password: newUser.password
        });
      }
      await loadUsers();
    } catch (error) {
      console.log("Add User Error", error);
      throw error; // Re-throw to handle in component
    }
  };

  const updateUser = async (id, updatedFields) => {
    try {
      if (updatedFields.role === "admin") {
        const payload = {
          username: updatedFields.username,
          email: updatedFields.email,
          company_id: updatedFields.companyId
        };
        if (updatedFields.password && updatedFields.password.trim() !== '') {
          payload.password = updatedFields.password;
        }
        await api.put(`/admin-users/${id}`, payload);
        
      } else if (updatedFields.role === "installer") {
        const payload = {
          username: updatedFields.username,
          phone: updatedFields.phone,
          company_id: updatedFields.companyId,
          sub_company_id: updatedFields.subCompanyId || null,
          teamlead_id: updatedFields.teamLeadId || null,
          mileage: updatedFields.mileage || null  // ✅ Added mileage
        };
        if (updatedFields.password && updatedFields.password.trim() !== '') {
          payload.password = updatedFields.password;
        }
        await api.put(`/installer/${id}`, payload);
        
      } else if (updatedFields.role === "teamlead") {
        const payload = {
          username: updatedFields.username
        };
        if (updatedFields.password) {
          payload.password = updatedFields.password;
        }
        await api.put(`/teamlead/${id}`, payload);
      }
      await loadUsers();
    } catch (error) {
      console.log("Update User Error", error);
      throw error;
    }
  };

  const deleteUser = async (id, role) => {
    try {
      if (role === "installer") {
        await api.delete(`/installer/${id}`);
      } else if (role === "admin") {
        await api.delete(`/admin-users/${id}`);
      } else if (role === "teamlead") {
        await api.delete(`/teamlead/${id}`);
      }
      await loadUsers();
    } catch (error) {
      console.log("Delete User Error", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, users, login, logout, addUser, deleteUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);